import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import {
  computeSoFacets,
  deskOpenUsesLocalCache,
  facetOptionLabel,
  filterDeskOpenSalesOrders,
  matchesDeliverAxisFilter,
  matchesDueFilter,
  matchesLifecycleFilter,
  matchesPayAxisFilter,
  primaryDueBadge,
  sortDeskOpenBy,
  type DeliverAxisFilter,
  type DueFilter,
  type LifecycleFilter,
  type PayAxisFilter,
} from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { fetchMe } from "@/shared/api/auth";
import { listSalesOrders, type SoListSort } from "@/shared/api/sales";
import {
  batchDocAssignees,
  batchDocTags,
  listTags,
  type AssigneeRow,
  type TagRow,
} from "@/shared/api/tags";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { AdminMoreFilters } from "@/modules/shell/AdminMoreFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import { PartyFilterChipSearch } from "@/modules/shell/PartyFilterChipSearch";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { ORDER_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import type { PartyFilter } from "@/modules/core/list-cell-actions";
import {
  listSearchDebounceMs,
  paramOr,
  readPartyFilter,
  readQ,
  setOrDelete,
  writeLifecycleParam,
  writePartyFilter,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { salesModuleTabs } from "@/modules/shell/desk-module-tabs";
import { PageFrame } from "@/modules/shell/PageFrame";
import { useDeskOpenCache } from "@/modules/shell/desk-sync/use-desk-open";
import { salesOrderColumns, type SoTableRow } from "./sales-orders-columns";

type AssigneeFilter = "all" | "mine" | "unassigned";

const LIFECYCLES = ["all", "draft", "open", "void", "done"] as const;
const PAYS = ["all", "unpaid", "partial", "paid"] as const;
const DELIVERS = ["all", "undelivered", "partial", "delivered"] as const;
const DUES = ["all", "this_week", "overdue", "none"] as const;
const ASSIGNEES = ["all", "mine", "unassigned"] as const;

const PAGE_SIZE = 50;
const SORTABLE: SoListSort[] = [
  "number",
  "customerName",
  "grossTotal",
  "lifecycle",
  "dueAt",
  "docDate",
];

function sortingToApi(sorting: SortingState): {
  sort: SoListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (
    first &&
    SORTABLE.includes(first.id as SoListSort)
  ) {
    return {
      sort: first.id as SoListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function SalesOrdersPage() {
  const { companySlug } = useParams();
  const location = useLocation();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();

  const tagId = searchParams.get("tag") ?? "";
  const assigneeFilter = paramOr(
    searchParams,
    "assignee",
    ASSIGNEES,
    "all",
  ) as AssigneeFilter;
  const meQuery = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    staleTime: 60_000,
    enabled: Boolean(companySlug) && assigneeFilter === "mine",
  });
  const meAvatarLabel =
    meQuery.data?.user.displayName?.trim() ||
    meQuery.data?.user.email ||
    "You";
  const dueFilter = paramOr(searchParams, "due", DUES, "all") as DueFilter;
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "open",
  ) as LifecycleFilter;
  const payFilter = paramOr(searchParams, "pay", PAYS, "all") as PayAxisFilter;
  const deliverFilter = paramOr(
    searchParams,
    "deliver",
    DELIVERS,
    "all",
  ) as DeliverAxisFilter;
  const partyFilter = readPartyFilter(searchParams);

  const setTagId = (next: string) =>
    patchParams((p) => setOrDelete(p, "tag", next));
  const setAssigneeFilter = (next: AssigneeFilter) =>
    patchParams((p) => setOrDelete(p, "assignee", next));
  const setDueFilter = (next: DueFilter) =>
    patchParams((p) => setOrDelete(p, "due", next));
  const setLifecycleFilter = (next: LifecycleFilter) =>
    patchParams((p) => writeLifecycleParam(p, next));
  const setPayFilter = (next: PayAxisFilter) =>
    patchParams((p) => setOrDelete(p, "pay", next));
  const setDeliverFilter = (next: DeliverAxisFilter) =>
    patchParams((p) => setOrDelete(p, "deliver", next));
  const setPartyFilter = (next: PartyFilter | null) =>
    patchParams((p) => writePartyFilter(p, next));

  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  const useLocal = deskOpenUsesLocalCache({
    lifecycle: lifecycleFilter,
    tagId,
    assignee: assigneeFilter,
  });

  /** Keep search box in sync when saved views / back navigation rewrite URL. */
  useEffect(() => {
    const q = readQ(searchParams);
    setQuery((prev) => (prev === q ? prev : q));
    setDebouncedQ((prev) => (prev === q ? prev : q));
  }, [searchParams]);

  useEffect(() => {
    const ms = listSearchDebounceMs(useLocal);
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      if (readQ(searchParams) === q) return;
      patchParams((p) => setOrDelete(p, "q", q));
    }, ms);
    return () => window.clearTimeout(t);
  }, [query, patchParams, useLocal, searchParams]);

  const serverLifecycle =
    lifecycleFilter === "draft" ||
    lifecycleFilter === "open" ||
    lifecycleFilter === "void"
      ? lifecycleFilter
      : undefined;

  /** Instant local filter; debounced value for server / URL. */
  const searchQ = useLocal ? query.trim() : debouncedQ;

  const tagsQuery = useQuery({
    queryKey: ["tags", companySlug, "catalog"],
    enabled: Boolean(companySlug),
    queryFn: () => listTags(companySlug!),
  });

  /** Always hydrate desk-open for local list + cheap facets — plan/33+35 C. */
  const deskCacheQuery = useDeskOpenCache(companySlug, Boolean(companySlug));

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.salesOrders.list(companySlug ?? "", {
      tagId,
      assigneeFilter,
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug) && !useLocal,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const list = await listSalesOrders(companySlug!, {
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        lifecycle: serverLifecycle,
        tagId: tagId || undefined,
        assignedToMe: assigneeFilter === "mine",
        unassigned: assigneeFilter === "unassigned",
        sort,
        sortDir,
        signal,
      });
      const ids = list.orders.map((o) => o.id);
      const [tagsBatch, assigneesBatch] = await Promise.all([
        batchDocTags(companySlug!, "sales_order", ids),
        batchDocAssignees(companySlug!, "sales_order", ids),
      ]);
      return {
        ...list,
        tagsByDoc: tagsBatch.byDoc,
        assigneesByDoc: assigneesBatch.byDoc,
      };
    },
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const localOrders = useMemo(() => {
    if (!useLocal || !deskCacheQuery.data) return [];
    const filtered = filterDeskOpenSalesOrders(
      deskCacheQuery.data.salesOrders,
      {
        q: searchQ,
        lifecycle: lifecycleFilter,
        pay: payFilter,
        deliver: deliverFilter,
        partyId: partyFilter?.id,
        due: dueFilter,
      },
    );
    return sortDeskOpenBy(filtered, sort, sortDir, (r) => {
      if (sort === "customerName") return r.customerName.toLowerCase();
      if (sort === "grossTotal") return r.grossTotal;
      if (sort === "lifecycle") return r.lifecycle;
      if (sort === "docDate") {
        const raw = r.docDate;
        if (raw == null || raw === "") return "0000-00-00";
        return String(raw).slice(0, 10);
      }
      if (sort === "dueAt") {
        const raw = r.paymentDueOn;
        if (raw == null || raw === "") return "9999-12-31";
        return String(raw).slice(0, 10);
      }
      return r.number.toLowerCase();
    });
  }, [
    useLocal,
    deskCacheQuery.data,
    searchQ,
    lifecycleFilter,
    payFilter,
    deliverFilter,
    partyFilter?.id,
    dueFilter,
    sort,
    sortDir,
  ]);

  const localMetaQuery = useQuery({
    queryKey: [
      "deskOpen",
      companySlug,
      "soMeta",
      localOrders.map((o) => o.id).join(","),
    ],
    enabled: Boolean(companySlug) && useLocal && localOrders.length > 0,
    queryFn: async () => {
      const ids = localOrders.map((o) => o.id);
      const [tagsBatch, assigneesBatch] = await Promise.all([
        batchDocTags(companySlug!, "sales_order", ids),
        batchDocAssignees(companySlug!, "sales_order", ids),
      ]);
      return {
        tagsByDoc: tagsBatch.byDoc,
        assigneesByDoc: assigneesBatch.byDoc,
      };
    },
  });

  const { flashId, flashRow } = useListRowFlash();
  const listPath = companySlug
    ? `/c/${companySlug}/sales/orders${location.search}`
    : "";
  const listReturnHandled = useRef(false);

  const columns = useMemo(
    () =>
      companySlug
        ? salesOrderColumns(companySlug, {
            partyFilter,
            setPartyFilter,
            payFilter,
            setPayFilter,
            deliverFilter,
            setDeliverFilter,
            dueFilter,
            setDueFilter,
            tagId,
            setTagId,
            onFilterFromRow: flashRow,
            listPath,
          })
        : [],
    // setters are stable via patchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companySlug,
      partyFilter,
      payFilter,
      deliverFilter,
      dueFilter,
      tagId,
      flashRow,
      listPath,
    ],
  );

  const loading = useLocal
    ? deskCacheQuery.isPending
    : listQuery.isPending;

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(`/c/${companySlug}/sales/orders`);
    if (id) flashRow(id);
  }, [companySlug, loading, flashRow]);

  const errorMsg = queryErrorMessage(
    (useLocal ? deskCacheQuery.error : listQuery.error) ?? tagsQuery.error,
    (useLocal ? deskCacheQuery.isError : listQuery.isError) ||
      tagsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const filtersActive =
    Boolean(query.trim()) ||
    Boolean(tagId) ||
    Boolean(partyFilter) ||
    assigneeFilter !== "all" ||
    dueFilter !== "all" ||
    lifecycleFilter !== "open" ||
    payFilter !== "all" ||
    deliverFilter !== "all";
  const filtersUnrestricted =
    !query.trim() &&
    !tagId &&
    !partyFilter &&
    assigneeFilter === "all" &&
    dueFilter === "all" &&
    lifecycleFilter === "all" &&
    payFilter === "all" &&
    deliverFilter === "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery,
    canResetToDefault: filtersActive,
    canClearAll: !filtersUnrestricted,
    clearAllSetsLifecycleAll: true,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: listQuery.isFetching,
  });
  const moreFiltersActive =
    (tagId ? 1 : 0) +
    (assigneeFilter !== "all" ? 1 : 0) +
    (dueFilter !== "all" ? 1 : 0) +
    (deliverFilter !== "all" ? 1 : 0);

  const facets = useMemo(() => {
    if (!deskCacheQuery.data) return null;
    return computeSoFacets(deskCacheQuery.data.salesOrders, {
      q: searchQ,
      lifecycle: lifecycleFilter,
      pay: payFilter,
      deliver: deliverFilter,
      partyId: partyFilter?.id,
      due: dueFilter,
    });
  }, [
    deskCacheQuery.data,
    searchQ,
    lifecycleFilter,
    payFilter,
    deliverFilter,
    partyFilter?.id,
    dueFilter,
  ]);

  if (!companySlug) return null;

  const orders = useLocal
    ? localOrders
    : (listQuery.data?.pages.flatMap((p) => p.orders) ?? []);
  const tagsByDoc: Record<string, TagRow[]> = {};
  const assigneesByDoc: Record<string, AssigneeRow[]> = {};
  if (useLocal) {
    Object.assign(tagsByDoc, localMetaQuery.data?.tagsByDoc ?? {});
    Object.assign(assigneesByDoc, localMetaQuery.data?.assigneesByDoc ?? {});
  } else {
    for (const page of listQuery.data?.pages ?? []) {
      Object.assign(tagsByDoc, page.tagsByDoc);
      Object.assign(assigneesByDoc, page.assigneesByDoc ?? {});
    }
  }
  const catalog = tagsQuery.data?.tags ?? [];
  const total = useLocal
    ? localOrders.length
    : listQuery.data?.pages[0]?.total;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  const withMeta = (o: (typeof orders)[number]): SoTableRow => ({
    ...o,
    tags: tagsByDoc[o.id] ?? [],
    assignees: assigneesByDoc[o.id] ?? [],
  });

  const visible: SoTableRow[] = useLocal
    ? localOrders.map(withMeta)
    : orders
        .filter((o) => {
          if (partyFilter && o.customerId !== partyFilter.id) return false;
          if (
            !matchesLifecycleFilter(o.lifecycle, lifecycleFilter, {
              pay: o.pay,
              deliver: o.deliver,
            })
          ) {
            return false;
          }
          if (!matchesPayAxisFilter(o.pay, payFilter)) return false;
          if (!matchesDeliverAxisFilter(o.deliver, deliverFilter)) return false;
          return matchesDueFilter(
            primaryDueBadge({
              paymentDueOn: o.paymentDueOn,
              deliverBy: o.deliverBy,
            }),
            dueFilter,
          );
        })
        .map(withMeta);

  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="Sales orders"
        description="Sell → deliver → collect (no drawer)"
        tabs={
          <DeskSubNav
            placement="header"
            items={salesModuleTabs(companySlug)}
            aria-label="Sales views"
          />
        }
        primaryAction={
          <Button asChild>
            <Link
              to={`/c/${companySlug}/sales/orders/new`}
              className="no-underline"
            >
              New SO
            </Link>
          </Button>
        }
      />

      <AdminFilters>
        <input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search number, customer, ref…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading && orders.length === 0}
        />
        <select
          className={adminFilterControlClass}
          value={lifecycleFilter}
          onChange={(e) =>
            setLifecycleFilter(e.target.value as LifecycleFilter)
          }
        >
          <option value="all">
            {facetOptionLabel("All lifecycle", facets?.lifecycle.all)}
          </option>
          <option value="draft">
            {facetOptionLabel("Draft", facets?.lifecycle.draft)}
          </option>
          <option value="open">
            {facetOptionLabel("Posted", facets?.lifecycle.open)}
          </option>
          <option value="void">
            {facetOptionLabel("Void", facets?.lifecycle.void)}
          </option>
          <option value="done">
            {facetOptionLabel("Done", facets?.lifecycle.done)}
          </option>
        </select>
        <select
          className={adminFilterControlClass}
          value={payFilter}
          onChange={(e) => setPayFilter(e.target.value as PayAxisFilter)}
        >
          <option value="all">
            {facetOptionLabel("All pay", facets?.pay.all)}
          </option>
          <option value="unpaid">
            {facetOptionLabel("Unpaid", facets?.pay.unpaid)}
          </option>
          <option value="partial">
            {facetOptionLabel("Partial pay", facets?.pay.partial)}
          </option>
          <option value="paid">
            {facetOptionLabel("Paid", facets?.pay.paid)}
          </option>
        </select>
        <AdminMoreFilters activeCount={moreFiltersActive}>
          <select
            className={adminFilterControlClass}
            value={deliverFilter}
            onChange={(e) =>
              setDeliverFilter(e.target.value as DeliverAxisFilter)
            }
          >
            <option value="all">
              {facetOptionLabel("All delivery", facets?.fulfill.all)}
            </option>
            <option value="undelivered">
              {facetOptionLabel("Undelivered", facets?.fulfill.undelivered)}
            </option>
            <option value="partial">
              {facetOptionLabel("Partial deliv", facets?.fulfill.partial)}
            </option>
            <option value="delivered">
              {facetOptionLabel("Delivered", facets?.fulfill.delivered)}
            </option>
          </select>
          <select
            className={adminFilterControlClass}
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
          >
            <option value="">All tags</option>
            {catalog.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            className={adminFilterControlClass}
            value={assigneeFilter}
            onChange={(e) =>
              setAssigneeFilter(e.target.value as AssigneeFilter)
            }
          >
            <option value="all">All assignees</option>
            <option value="mine">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <select
            className={adminFilterControlClass}
            value={dueFilter}
            onChange={(e) => setDueFilter(e.target.value as DueFilter)}
          >
            <option value="all">
              {facetOptionLabel("All dues", facets?.due.all)}
            </option>
            <option value="this_week">
              {facetOptionLabel("Due this week", facets?.due.this_week)}
            </option>
            <option value="overdue">
              {facetOptionLabel("Overdue", facets?.due.overdue)}
            </option>
            <option value="none">
              {facetOptionLabel("No due date", facets?.due.none)}
            </option>
          </select>
        </AdminMoreFilters>
        <SavedListViewsControl
          listId="sales-orders"
          companySlug={companySlug}
          currentQs={searchParams.toString()}
          onApplyQs={(qs) => {
            replaceAllParams(qs);
            const q = readQ(new URLSearchParams(qs));
            setQuery(q);
            setDebouncedQ(q);
          }}
        />
        <ActiveFilterChips
          chips={[
            ...(partyFilter && companySlug
              ? [
                  {
                    key: "party",
                    label: `Customer: ${partyFilter.name}`,
                    avatars: [{ label: partyFilter.name }],
                    onClear: () => setPartyFilter(null),
                    editPanel: (close: () => void) => (
                      <PartyFilterChipSearch
                        companySlug={companySlug}
                        role="customer"
                        currentId={partyFilter.id}
                        onPick={setPartyFilter}
                        onClose={close}
                      />
                    ),
                    help: {
                      title: "Customer filter",
                      summary:
                        "Showing sales orders for this customer only. Click the chip to switch customer, or × to clear.",
                    },
                  },
                ]
              : []),
            ...(lifecycleFilter !== "open"
              ? [
                  {
                    key: "lifecycle",
                    label:
                      lifecycleFilter === "all"
                        ? "All lifecycle"
                        : lifecycleFilter === "done"
                          ? "Done"
                          : lifecycleFilter[0]!.toUpperCase() +
                            lifecycleFilter.slice(1),
                    value: lifecycleFilter,
                    choices: [
                      {
                        value: "all",
                        label: facetOptionLabel(
                          "All lifecycle",
                          facets?.lifecycle.all,
                        ),
                      },
                      {
                        value: "draft",
                        label: facetOptionLabel(
                          "Draft",
                          facets?.lifecycle.draft,
                        ),
                      },
                      {
                        value: "open",
                        label: facetOptionLabel(
                          "Posted",
                          facets?.lifecycle.open,
                        ),
                      },
                      {
                        value: "void",
                        label: facetOptionLabel(
                          "Void",
                          facets?.lifecycle.void,
                        ),
                      },
                      {
                        value: "done",
                        label: facetOptionLabel(
                          "Done",
                          facets?.lifecycle.done,
                        ),
                      },
                    ],
                    onChange: (v: string) =>
                      setLifecycleFilter(v as LifecycleFilter),
                    onClear: () => setLifecycleFilter("open"),
                  },
                ]
              : []),
            ...(payFilter !== "all"
              ? [
                  {
                    key: "pay",
                    label:
                      payFilter === "unpaid"
                        ? "Unpaid"
                        : payFilter === "partial"
                          ? "Partial pay"
                          : "Paid",
                    value: payFilter,
                    choices: [
                      {
                        value: "all",
                        label: facetOptionLabel("All pay", facets?.pay.all),
                      },
                      {
                        value: "unpaid",
                        label: facetOptionLabel("Unpaid", facets?.pay.unpaid),
                      },
                      {
                        value: "partial",
                        label: facetOptionLabel(
                          "Partial pay",
                          facets?.pay.partial,
                        ),
                      },
                      {
                        value: "paid",
                        label: facetOptionLabel("Paid", facets?.pay.paid),
                      },
                    ],
                    onChange: (v: string) =>
                      setPayFilter(v as PayAxisFilter),
                    onClear: () => setPayFilter("all"),
                  },
                ]
              : []),
            ...(deliverFilter !== "all"
              ? [
                  {
                    key: "deliver",
                    label:
                      deliverFilter === "undelivered"
                        ? "Undelivered"
                        : deliverFilter === "partial"
                          ? "Partial deliv"
                          : "Delivered",
                    value: deliverFilter,
                    choices: [
                      {
                        value: "all",
                        label: facetOptionLabel(
                          "All delivery",
                          facets?.fulfill.all,
                        ),
                      },
                      {
                        value: "undelivered",
                        label: facetOptionLabel(
                          "Undelivered",
                          facets?.fulfill.undelivered,
                        ),
                      },
                      {
                        value: "partial",
                        label: facetOptionLabel(
                          "Partial deliv",
                          facets?.fulfill.partial,
                        ),
                      },
                      {
                        value: "delivered",
                        label: facetOptionLabel(
                          "Delivered",
                          facets?.fulfill.delivered,
                        ),
                      },
                    ],
                    onChange: (v: string) =>
                      setDeliverFilter(v as DeliverAxisFilter),
                    onClear: () => setDeliverFilter("all"),
                  },
                ]
              : []),
            ...(tagId
              ? [
                  {
                    key: "tag",
                    label: `Tag: ${catalog.find((t) => t.id === tagId)?.name ?? tagId}`,
                    value: tagId,
                    choices: [
                      { value: "", label: "All tags" },
                      ...catalog.map((t) => ({
                        value: t.id,
                        label: t.name,
                      })),
                    ],
                    onChange: (v: string) => setTagId(v),
                    onClear: () => setTagId(""),
                  },
                ]
              : []),
            ...(assigneeFilter !== "all"
              ? [
                  {
                    key: "assignee",
                    label:
                      assigneeFilter === "mine"
                        ? "Assigned to me"
                        : "Unassigned",
                    ...(assigneeFilter === "mine"
                      ? { avatars: [{ label: meAvatarLabel }] }
                      : {}),
                    value: assigneeFilter,
                    choices: [
                      { value: "all", label: "All assignees" },
                      { value: "mine", label: "Assigned to me" },
                      { value: "unassigned", label: "Unassigned" },
                    ],
                    onChange: (v: string) =>
                      setAssigneeFilter(v as AssigneeFilter),
                    onClear: () => setAssigneeFilter("all"),
                    help: {
                      title:
                        assigneeFilter === "mine"
                          ? "Assigned to me"
                          : "Unassigned",
                      summary:
                        assigneeFilter === "mine"
                          ? "Showing orders where you are an assignee. Click the chip to switch, or × to clear."
                          : "Showing orders with no assignee. Click the chip to switch, or × to clear.",
                    },
                  },
                ]
              : []),
            ...(dueFilter !== "all"
              ? [
                  {
                    key: "due",
                    label:
                      dueFilter === "this_week"
                        ? "Due this week"
                        : dueFilter === "overdue"
                          ? "Overdue"
                          : "No due date",
                    value: dueFilter,
                    choices: [
                      {
                        value: "all",
                        label: facetOptionLabel("All dues", facets?.due.all),
                      },
                      {
                        value: "this_week",
                        label: facetOptionLabel(
                          "Due this week",
                          facets?.due.this_week,
                        ),
                      },
                      {
                        value: "overdue",
                        label: facetOptionLabel(
                          "Overdue",
                          facets?.due.overdue,
                        ),
                      },
                      {
                        value: "none",
                        label: facetOptionLabel(
                          "No due date",
                          facets?.due.none,
                        ),
                      },
                    ],
                    onChange: (v: string) => setDueFilter(v as DueFilter),
                    onClear: () => setDueFilter("all"),
                  },
                ]
              : []),
          ]}
        />
      </AdminFilters>

      <DocListLegend
        kinds={ORDER_LIFECYCLE_LEGEND}
        showFulfillmentComplete
      />

      <ListSearchStatus online={!useLocal} searching={searchingOnline} />

      {!loading && orders.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {visible.length}
          {total > orders.length ? ` · loaded ${orders.length} of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && orders.length === 0 && (
        <Alert variant="muted" className="mt-8">
          Loading sales orders…
        </Alert>
      )}

      {!loading && visible.length === 0 && !error ? (
        <div className="mt-8">
          <EmptyState
            title={
              filtersActive ? "No orders match" : "No sales orders yet"
            }
            description={
              filtersActive
                ? "Try clearing search, lifecycle, pay, delivery, tags, assignee, or due filters — or Load more if axis filters are on."
                : "Counter sales are listed under POS sales — not here. Use an SO when you need deliver / collect later."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button asChild size="sm">
                    <Link
                      to={`/c/${companySlug}/sales/orders/new`}
                      className="no-underline"
                    >
                      New SO
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={`/c/${companySlug}/pos/tickets`}
                      className="no-underline"
                    >
                      POS sales
                    </Link>
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : null}

      {visible.length > 0 ? (
        <>
          <DataTable
            listId="sales-orders"
            companySlug={companySlug}
            columns={columns}
            data={visible}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            highlightedRowId={flashId}
            defaultVisibility={{ tags: false, assignees: false }}
            onRefresh={() =>
              void (useLocal
                ? deskCacheQuery.refetch()
                : listQuery.refetch())
            }
          />
          {!useLocal && listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={listQuery.isFetchingNextPage}
                pending={listQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void listQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
      </DataTableChrome>
    </PageFrame>
  );
}
